import { EmbedFrame } from "@/components/embed-frame";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import type { ComponentStyleType } from "@/lib/types/flow";

type EmbedPreviewProps = {
  html: string;
  /** Shown when there is nothing to preview yet. */
  emptyLabel: string;
  /** Platform frame — drawn around the widget, exactly as `EmbedRenderer` does. */
  frameStyle?: ComponentStyleType;
  title?: string;
  description?: string;
  /** Forced by the preview dialog when comparing light against dark. */
  theme?: "auto" | "light" | "dark";
};

/**
 * Live preview of embed HTML (QL-178). Uses the same `EmbedFrame` as the flow
 * renderer, so what the designer sees here is what the client gets — including
 * whether the widget's script actually runs.
 */
export function EmbedPreview({
  html,
  emptyLabel,
  frameStyle = "none",
  title,
  description,
  theme = "auto",
}: EmbedPreviewProps) {
  if (!html.trim()) {
    return (
      <div className="flex h-24 items-center justify-center rounded-md border border-dashed">
        <p className="text-xs text-muted-foreground">{emptyLabel}</p>
      </div>
    );
  }

  // The frame, title and description belong to the platform, not to the
  // generated HTML — mirror `EmbedRenderer`.
  const wrapperClass =
    frameStyle === "border"
      ? "border rounded-lg p-4"
      : frameStyle === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";

  return (
    <div className={wrapperClass || undefined}>
      <FlowComponentHeader title={title} description={description} className="mb-4" />
      <EmbedFrame html={html} theme={theme} />
    </div>
  );
}
